/**
 * Screenshot Capture Hook
 *
 * Provides screenshot capture functionality using html2canvas.
 * Supports full page, viewport, and element capture with annotation support.
 */

import { useState, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';

export type CaptureMode = 'full_page' | 'viewport' | 'element';

export interface Screenshot {
  id: string;
  dataUrl: string;
  width: number;
  height: number;
  captureMode: CaptureMode;
  capturedAt: Date;
  annotations?: Annotation[];
}

export interface Annotation {
  id: string;
  type: 'rectangle' | 'arrow' | 'text' | 'blur';
  x: number;
  y: number;
  width?: number;
  height?: number;
  endX?: number;
  endY?: number;
  text?: string;
  color?: string;
}

export interface ScreenshotCaptureOptions {
  maxScreenshots?: number;
  maxSizeBytes?: number;
  quality?: number;
  captureMode?: CaptureMode;
  excludeSelectors?: string[];
}

const DEFAULT_OPTIONS: Required<ScreenshotCaptureOptions> = {
  maxScreenshots: 5,
  maxSizeBytes: 5 * 1024 * 1024, // 5MB
  quality: 0.92,
  captureMode: 'viewport',
  excludeSelectors: ['.psc-dialog-overlay', '.psc-floating-button', '[data-psc-exclude]'],
};

/**
 * Generate unique ID for screenshots
 */
const generateId = (): string => {
  return `screenshot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Calculate base64 string size in bytes
 */
const calculateBase64Size = (base64: string): number => {
  // Remove data URI prefix if present
  const base64Data = base64.split(',')[1] || base64;
  // Base64 encodes 3 bytes into 4 characters
  return Math.ceil(base64Data.length * 0.75);
};

/**
 * Hook for capturing screenshots with annotation support
 */
export function useScreenshotCapture(options: ScreenshotCaptureOptions = {}) {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeScreenshot, setActiveScreenshot] = useState<Screenshot | null>(null);

  const elementRef = useRef<HTMLElement | null>(null);

  /**
   * Hide PSC elements before capture, restore after
   */
  const withHiddenElements = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T> => {
      const hiddenElements: HTMLElement[] = [];

      // Hide excluded elements
      config.excludeSelectors.forEach((selector) => {
        document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
          if (el.style.visibility !== 'hidden') {
            hiddenElements.push(el);
            el.style.visibility = 'hidden';
          }
        });
      });

      try {
        return await fn();
      } finally {
        // Restore visibility
        hiddenElements.forEach((el) => {
          el.style.visibility = '';
        });
      }
    },
    [config.excludeSelectors]
  );

  /**
   * Capture screenshot based on mode
   */
  const captureScreenshot = useCallback(
    async (mode?: CaptureMode, targetElement?: HTMLElement): Promise<Screenshot | null> => {
      const captureMode = mode || config.captureMode;

      if (screenshots.length >= config.maxScreenshots) {
        setError(`Maximum ${config.maxScreenshots} screenshots allowed`);
        return null;
      }

      setIsCapturing(true);
      setError(null);

      try {
        const screenshot = await withHiddenElements(async () => {
          let canvas: HTMLCanvasElement;
          let element: HTMLElement;

          switch (captureMode) {
            case 'full_page':
              // Capture entire document body
              element = document.body;
              canvas = await html2canvas(element, {
                useCORS: true,
                allowTaint: false,
                logging: false,
                scrollX: 0,
                scrollY: 0,
                windowWidth: document.documentElement.scrollWidth,
                windowHeight: document.documentElement.scrollHeight,
              });
              break;

            case 'element':
              // Capture specific element
              element = targetElement || elementRef.current || document.body;
              canvas = await html2canvas(element, {
                useCORS: true,
                allowTaint: false,
                logging: false,
              });
              break;

            case 'viewport':
            default:
              // Capture visible viewport only
              element = document.body;
              canvas = await html2canvas(element, {
                useCORS: true,
                allowTaint: false,
                logging: false,
                scrollX: window.scrollX,
                scrollY: window.scrollY,
                windowWidth: window.innerWidth,
                windowHeight: window.innerHeight,
                width: window.innerWidth,
                height: window.innerHeight,
                x: window.scrollX,
                y: window.scrollY,
              });
              break;
          }

          // Convert to data URL with quality
          const dataUrl = canvas.toDataURL('image/png', config.quality);

          // Check file size
          const sizeBytes = calculateBase64Size(dataUrl);
          if (sizeBytes > config.maxSizeBytes) {
            throw new Error(
              `Screenshot size (${(sizeBytes / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed (${(config.maxSizeBytes / 1024 / 1024).toFixed(2)}MB)`
            );
          }

          const newScreenshot: Screenshot = {
            id: generateId(),
            dataUrl,
            width: canvas.width,
            height: canvas.height,
            captureMode,
            capturedAt: new Date(),
            annotations: [],
          };

          return newScreenshot;
        });

        setScreenshots((prev) => [...prev, screenshot]);
        setActiveScreenshot(screenshot);
        return screenshot;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to capture screenshot';
        setError(errorMessage);
        return null;
      } finally {
        setIsCapturing(false);
      }
    },
    [config, screenshots.length, withHiddenElements]
  );

  /**
   * Capture full page screenshot
   */
  const captureFullPage = useCallback(async (): Promise<Screenshot | null> => {
    return captureScreenshot('full_page');
  }, [captureScreenshot]);

  /**
   * Capture visible viewport screenshot
   */
  const captureViewport = useCallback(async (): Promise<Screenshot | null> => {
    return captureScreenshot('viewport');
  }, [captureScreenshot]);

  /**
   * Capture specific element screenshot
   */
  const captureElement = useCallback(
    async (element: HTMLElement): Promise<Screenshot | null> => {
      return captureScreenshot('element', element);
    },
    [captureScreenshot]
  );

  /**
   * Delete a screenshot by ID
   */
  const deleteScreenshot = useCallback(
    (id: string): void => {
      setScreenshots((prev) => prev.filter((s) => s.id !== id));
      if (activeScreenshot?.id === id) {
        setActiveScreenshot(null);
      }
    },
    [activeScreenshot]
  );

  /**
   * Clear all screenshots
   */
  const clearScreenshots = useCallback((): void => {
    setScreenshots([]);
    setActiveScreenshot(null);
    setError(null);
  }, []);

  /**
   * Add annotation to a screenshot
   */
  const addAnnotation = useCallback((screenshotId: string, annotation: Omit<Annotation, 'id'>): void => {
    const newAnnotation: Annotation = {
      ...annotation,
      id: `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    setScreenshots((prev) =>
      prev.map((s) =>
        s.id === screenshotId ? { ...s, annotations: [...(s.annotations || []), newAnnotation] } : s
      )
    );

    // Update active screenshot if it matches
    setActiveScreenshot((prev) =>
      prev?.id === screenshotId
        ? { ...prev, annotations: [...(prev.annotations || []), newAnnotation] }
        : prev
    );
  }, []);

  /**
   * Remove annotation from a screenshot
   */
  const removeAnnotation = useCallback((screenshotId: string, annotationId: string): void => {
    setScreenshots((prev) =>
      prev.map((s) =>
        s.id === screenshotId
          ? { ...s, annotations: (s.annotations || []).filter((a) => a.id !== annotationId) }
          : s
      )
    );

    setActiveScreenshot((prev) =>
      prev?.id === screenshotId
        ? { ...prev, annotations: (prev.annotations || []).filter((a) => a.id !== annotationId) }
        : prev
    );
  }, []);

  /**
   * Update screenshot with annotations applied (flattened)
   */
  const flattenAnnotations = useCallback((screenshotId: string, flattenedDataUrl: string): void => {
    setScreenshots((prev) =>
      prev.map((s) =>
        s.id === screenshotId ? { ...s, dataUrl: flattenedDataUrl, annotations: [] } : s
      )
    );

    setActiveScreenshot((prev) =>
      prev?.id === screenshotId ? { ...prev, dataUrl: flattenedDataUrl, annotations: [] } : prev
    );
  }, []);

  /**
   * Get screenshot by ID
   */
  const getScreenshot = useCallback(
    (id: string): Screenshot | undefined => {
      return screenshots.find((s) => s.id === id);
    },
    [screenshots]
  );

  /**
   * Get total size of all screenshots in bytes
   */
  const getTotalSize = useCallback((): number => {
    return screenshots.reduce((total, s) => total + calculateBase64Size(s.dataUrl), 0);
  }, [screenshots]);

  /**
   * Check if more screenshots can be captured
   */
  const canCapture = useCallback((): boolean => {
    return screenshots.length < config.maxScreenshots;
  }, [screenshots.length, config.maxScreenshots]);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    // State
    screenshots,
    activeScreenshot,
    isCapturing,
    error,

    // Capture methods
    captureScreenshot,
    captureFullPage,
    captureViewport,
    captureElement,

    // Management methods
    deleteScreenshot,
    clearScreenshots,
    setActiveScreenshot,
    getScreenshot,

    // Annotation methods
    addAnnotation,
    removeAnnotation,
    flattenAnnotations,

    // Utilities
    getTotalSize,
    canCapture,
    clearError,

    // Ref for element capture
    elementRef,

    // Config
    maxScreenshots: config.maxScreenshots,
    maxSizeBytes: config.maxSizeBytes,
  };
}

export type UseScreenshotCapture = ReturnType<typeof useScreenshotCapture>;
